import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, XCircle } from 'lucide-react';

export const Route = createFileRoute('/confirm-email')({
  component: ConfirmEmailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    userId: typeof search.userId === 'string' ? search.userId : '',
    token: typeof search.token === 'string' ? search.token : '',
  }),
});

function ConfirmEmailPage() {
  const { userId, token } = Route.useSearch();
  const confirm = useMutation({
    mutationFn: () => api('/auth/confirm-email', { method: 'POST', json: { userId, token } }),
  });

  useEffect(() => {
    if (userId && token) confirm.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  return (
    <div className="grid min-h-full place-items-center p-8">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-base">Email confirmation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!userId || !token ? (
            <p className="text-(--color-muted-foreground)">This confirmation link is missing required parameters.</p>
          ) : confirm.isPending ? (
            <p className="text-(--color-muted-foreground)">Confirming…</p>
          ) : confirm.isSuccess ? (
            <div className="flex items-center gap-2 text-(--color-success)">
              <CheckCircle2 className="size-4" /> Email confirmed. You can now sign in.
            </div>
          ) : confirm.isError ? (
            <div className="flex items-center gap-2 text-(--color-danger)">
              <XCircle className="size-4" /> Confirmation failed. The link may have expired.
            </div>
          ) : null}
          <p className="text-center text-xs text-(--color-muted-foreground)">
            <Link to="/login" className="hover:underline">Continue to sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
